'use client'

import { useEffect, useState } from 'react'
import Image from 'next/image'

const SCRIPTS = [
  {
    heard: 'Prep slides for Monday review',
    task: { emoji: '📊', title: 'Prep slides for Monday review', priority: 'High',   time: '9:00 AM' },
  },
  {
    heard: 'Call Mom tonight at 8',
    task: { emoji: '📞', title: 'Call Mom',                     priority: 'Medium', time: '8:00 PM' },
  },
  {
    heard: 'Dentist next Tuesday at 4 PM',
    task: { emoji: '🦷', title: 'Dentist appointment',          priority: 'Medium', time: '4:00 PM' },
  },
]

function I({ name, size = 18, stroke = 2 }) {
  const c = {
    width: size, height: size, viewBox: '0 0 24 24', fill: 'none',
    stroke: 'currentColor', strokeWidth: stroke,
    strokeLinecap: 'round', strokeLinejoin: 'round',
  }
  switch (name) {
    case 'mic':   return (<svg {...c}><rect x="9" y="3" width="6" height="12" rx="3"/><path d="M5 11a7 7 0 0 0 14 0"/><path d="M12 18v3"/></svg>)
    case 'check': return (<svg {...c}><path d="M5 12l5 5 9-11"/></svg>)
    case 'apple': return (<svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M17.05 20.28c-.98.95-2.05.88-3.08.35-1.09-.55-2.08-.54-3.24 0-1.44.68-2.2.53-3.06-.35C2.79 15.15 3.51 7.46 9.05 7.12c1.35.07 2.29.74 3.08.74.77 0 2.21-.87 3.85-.72 1.33.13 2.53.71 3.27 1.8-3.02 1.81-2.59 5.83.51 6.97-.59 1.52-1.35 3.02-2.71 4.37zM12.03 7.08c-.14-2.28 1.78-4.14 3.93-4.29.29 2.59-2.37 4.53-3.93 4.29z"/></svg>)
    case 'android': return (<svg {...c}><path d="M5 17V11a7 7 0 0 1 14 0v6z"/><path d="M8 17v3"/><path d="M16 17v3"/><path d="M8 8 6 5"/><path d="M16 8l2-3"/></svg>)
    case 'mac':   return (<svg {...c}><rect x="2.5" y="4" width="19" height="12" rx="1.5"/><path d="M3 16h18"/><path d="M9 20h6"/></svg>)
    case 'chrome': return (<svg {...c}><circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="3.2"/><path d="M12 9h8.5"/><path d="M9.4 13.5 5 20"/><path d="M14.6 13.5 19 20"/></svg>)
    case 'arrow':  return (<svg {...c}><path d="M5 12h14"/><path d="M13 6l6 6-6 6"/></svg>)
    default:      return null
  }
}

export default function LoginRightPane() {
  const [sIdx, setSIdx] = useState(0)
  const [stage, setStage] = useState('listen') // listen | think | done
  const [typed, setTyped] = useState('')

  useEffect(() => {
    let cancelled = false
    const heard = SCRIPTS[sIdx].heard
    setStage('listen')
    setTyped('')

    const run = async () => {
      for (let i = 1; i <= heard.length; i++) {
        if (cancelled) return
        await new Promise((r) => setTimeout(r, 26))
        setTyped(heard.slice(0, i))
      }
      if (cancelled) return
      setStage('think')
      await new Promise((r) => setTimeout(r, 900))
      if (cancelled) return
      setStage('done')
      await new Promise((r) => setTimeout(r, 2600))
      if (cancelled) return
      setSIdx((i) => (i + 1) % SCRIPTS.length)
    }
    run()
    return () => { cancelled = true }
  }, [sIdx])

  const task = SCRIPTS[sIdx].task

  return (
    <div className="ava-login-right">
      <div className="ava-login-grain" aria-hidden />

      <div className="ava-login-content">
        <div className="ava-login-eyebrow">
          <span className="live-dot" />
          Voice AI &middot; Always on
        </div>

        <h2 className="ava-login-headline">
          Plan your day in <em>10&thinsp;seconds.</em>
        </h2>
        <p className="ava-login-sub">Just say it out loud &mdash; AVA captures, organizes, and acts.</p>

        <div className="ava-login-stage">
          <div className="ava-phone">
            <div className="ava-phone-screen">
              <div className="ava-phone-status">
                <span>9:41</span>
                <span className="ava-phone-status-r">
                  <span className="ava-phone-status-dot" /> AVA
                </span>
              </div>

              <div className="ava-phone-body">
                <div className="ava-phone-key">Tuesday &middot; Apr 23</div>
                <div className="ava-phone-greet">
                  Good morning, <em>Nora.</em>
                </div>

                <div className={'ava-phone-result ' + (stage === 'done' ? 'show' : '')}>
                  <div className="ava-phone-result-head">
                    <span className="ava-phone-result-ico">{task.emoji}</span>
                    <div className="ava-phone-result-body">
                      <div className="ava-phone-result-title">{task.title}</div>
                      <div className="ava-phone-result-meta">
                        {task.priority} &middot; {task.time}
                      </div>
                    </div>
                    <span className="ava-phone-result-check">
                      <I name="check" size={14} stroke={2.4} />
                    </span>
                  </div>
                </div>

                <div className={'ava-phone-transcript ' + (stage !== 'done' ? 'show' : '')}>
                  <span className="ava-phone-quote">
                    &ldquo;{typed}
                    {stage === 'listen' && <span className="ava-phone-caret" />}
                    &rdquo;
                  </span>
                </div>

                <div className="ava-phone-mic-dock">
                  <div className={'ava-phone-mic ' + stage}>
                    <I name={stage === 'done' ? 'check' : 'mic'} size={18} stroke={2} />
                  </div>
                  <div className="ava-phone-spectrum">
                    {Array.from({ length: 32 }).map((_, i) => (
                      <span key={i} style={{ animationDelay: `-${i * 0.05}s` }} className={stage} />
                    ))}
                  </div>
                </div>
              </div>
            </div>
            <div className="ava-phone-shadow" />
          </div>

        </div>

        <div className="ava-login-platforms" aria-label="Available platforms">
          <a
            href="https://apple.co/3Cmgy10"
            target="_blank"
            rel="noopener noreferrer"
            className="ava-login-platform"
            aria-label="Download on the App Store"
          >
            <span className="ava-login-platform-ico">
              <Image src="/icons/apple.svg" alt="" width={18} height={18} />
            </span>
            <span className="ava-login-platform-name">iOS</span>
          </a>
          <a
            href="https://play.google.com/store/apps/details?id=com.jegan.aiva&pcampaignid=web_share"
            target="_blank"
            rel="noopener noreferrer"
            className="ava-login-platform"
            aria-label="Get it on Google Play"
          >
            <span className="ava-login-platform-ico">
              <Image src="/icons/android.svg" alt="" width={18} height={18} />
            </span>
            <span className="ava-login-platform-name">Android</span>
          </a>
          <a
            href="https://apple.co/3Cmgy10"
            target="_blank"
            rel="noopener noreferrer"
            className="ava-login-platform"
            aria-label="Download for Mac"
          >
            <span className="ava-login-platform-ico">
              <Image src="/icons/apple.svg" alt="" width={18} height={18} />
            </span>
            <span className="ava-login-platform-name">Mac</span>
          </a>
          <a
            href="https://chromewebstore.google.com/detail/ava-ai-productivity-assis/dnfcodcajlmhgbobjicfblefnonhhfol?authuser=2&hl=en"
            target="_blank"
            rel="noopener noreferrer"
            className="ava-login-platform"
            aria-label="Install Chrome extension"
          >
            <span className="ava-login-platform-ico">
              <Image src="/icons/chrome.svg" alt="" width={18} height={18} />
            </span>
            <span className="ava-login-platform-name">Chrome</span>
          </a>
        </div>

        <div className="ava-login-foot">
          <div className="ava-login-foot-stat">
            <span className="num">50K+</span>
            <span>installs</span>
          </div>
          <div className="ava-login-foot-sep" />
          <div className="ava-login-foot-stat">
            <span className="num">4.7</span>
            <span>&#9733; App Store</span>
          </div>
          <div className="ava-login-foot-sep" />
          <div className="ava-login-foot-stat">
            <span className="num">12+</span>
            <span>hrs/wk saved</span>
          </div>
        </div>
      </div>
    </div>
  )
}
